import { useState } from 'react';
import { Popover, Stack, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HotelIcon from '@mui/icons-material/Hotel';
import PeopleIcon from '@mui/icons-material/People';
import ContadorPasajeros from './ContadorPasajeros';

const HabitacionesSelector = ({
  rooms,
  setRooms,
  adultsPerRoom,
  setAdultsPerRoom,
  childrenPerRoom,
  setChildrenPerRoom,
  childrenAges,
  setChildrenAges,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'habitaciones-popover' : undefined;

  const handleRoomsChange = (value) => {
    const newRooms = Math.max(1, Math.min(value, 5));
    setRooms(newRooms);

    setAdultsPerRoom((prev) => {
      const newAdults = [...prev];
      while (newAdults.length < newRooms) newAdults.push(2);
      return newAdults.slice(0, newRooms);
    });
    setChildrenPerRoom((prev) => {
      const newChildren = [...prev];
      while (newChildren.length < newRooms) newChildren.push(0);
      return newChildren.slice(0, newRooms);
    });
    setChildrenAges((prev) => {
      const newAges = [...prev];
      while (newAges.length < newRooms) newAges.push([]);
      return newAges.slice(0, newRooms);
    });
  };

  const handleAdultsChange = (roomIndex, value) => {
    setAdultsPerRoom((prev) => {
      const newAdults = [...prev];
      newAdults[roomIndex] = Math.max(1, Math.min(value, 4));
      return newAdults;
    });
  };

  const handleChildrenChange = (roomIndex, value) => {
    const newValue = Math.max(0, Math.min(value, 4));
    setChildrenPerRoom((prev) => {
      const newChildren = [...prev];
      newChildren[roomIndex] = newValue;
      return newChildren;
    });
    setChildrenAges((prev) => {
      const newAges = [...prev];
      newAges[roomIndex] = Array.from({ length: newValue }).map((_, i) => newAges[roomIndex]?.[i] || 2);
      return newAges;
    });
  };

  const handleChildAgeChange = (roomIndex, childIndex, age) => {
    setChildrenAges((prev) => {
      const newAges = [...prev];
      if (!newAges[roomIndex]) newAges[roomIndex] = [];
      newAges[roomIndex][childIndex] = age;
      return newAges;
    });
  };

  const totalAdults = adultsPerRoom.reduce((sum, adults) => sum + adults, 0);
  const totalChildren = childrenPerRoom.reduce((sum, children) => sum + children, 0);
  const totalGuests = totalAdults + totalChildren;

  return (
    <>
      <Stack
        aria-describedby={id}
        className='border-[1px] border-[#ccc] rounded-[4px] cursor-pointer px-[10px] py-[8px]'
        onClick={handleClick}
        direction='row'
        justifyContent='center'
        alignItems='center'
        sx={{ width: '100%', maxWidth: '140px' }}
      >
        <Stack direction='row' spacing={2} alignItems='center'>
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <HotelIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <Typography color='#000000' fontSize={14} fontWeight={300}>
              {rooms}
            </Typography>
          </Stack>
          <Stack direction='row' spacing={0.5} alignItems='center'>
            <PeopleIcon sx={{ fontSize: 16, color: '#6b7280' }} />
            <Typography color='#000000' fontSize={14} fontWeight={300}>
              {totalGuests}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPopover-paper': {
            minWidth: '400px',
            maxWidth: '500px',
            maxHeight: '500px',
            overflowY: 'auto',
          },
        }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <ContadorPasajeros
            title='Habitaciones'
            quantity={rooms}
            setQuantity={handleRoomsChange}
            min={1}
            max={5}
          />

          {Array.from({ length: rooms }).map((_, roomIndex) => (
            <Stack
              key={roomIndex}
              spacing={2}
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: 2,
                backgroundColor: '#fafafa',
              }}
            >
              <Typography variant='subtitle1' fontWeight={600}>
                Habitación {roomIndex + 1}
              </Typography>

              <ContadorPasajeros
                title='Adultos'
                quantity={adultsPerRoom[roomIndex] || 2}
                setQuantity={(value) => handleAdultsChange(roomIndex, value)}
                min={1}
                max={4}
              />

              <ContadorPasajeros
                title='Niños'
                description='de 0 a 17 años'
                quantity={childrenPerRoom[roomIndex] || 0}
                setQuantity={(value) => handleChildrenChange(roomIndex, value)}
                min={0}
                max={4}
              />

              {childrenPerRoom[roomIndex] > 0 && (
                <Stack spacing={1} sx={{ pl: 2 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Edad de los niños al momento del check-in:
                  </Typography>
                  {Array.from({ length: childrenPerRoom[roomIndex] }).map((_, childIndex) => (
                    <FormControl key={childIndex} size='small' fullWidth>
                      <InputLabel>Niño {childIndex + 1}</InputLabel>
                      <Select
                        value={childrenAges[roomIndex]?.[childIndex] || 2}
                        onChange={(e) => handleChildAgeChange(roomIndex, childIndex, e.target.value)}
                        label={`Niño ${childIndex + 1}`}
                      >
                        {Array.from({ length: 18 }).map((_, age) => (
                          <MenuItem key={age} value={age}>
                            {age} {age === 1 ? 'año' : 'años'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </Popover>
    </>
  );
};

export default HabitacionesSelector;

